import { Box, Button, Drawer, Grid, IconButton, TextField, Typography } from "@mui/material";
import { IClient, IClientsRes } from "./types";
import { IColumn } from "../../components/Table/types";
import CustomTable from "../../components/Table";
import { ChangeEvent, ReactElement, useState } from "react";
import { AxiosResponse } from "axios";
import { deleteApiWithAuth, getApi } from "../../utils/apis";
import getAPIUrl from "../../config";
import Header from "../Header";
import MoreIcon from "../../assets/more.svg";
import { UserStatuses } from "../../utils/constants";
import styles from "./styles";
import TableHeader from "../../components/TableHeader";
import { useNavigate } from "react-router-dom";
import { MenuItems } from "../../components/Menu/types";
import EditIcon from "../../assets/pen-square.svg";
import TerminateIcon from "../../assets/calendar-clock.svg";
import { getApiErrorMessage } from "../../utils/commonHelpers";
import { SimpleMenu } from "../../components/Menu";
import Toast from "../../components/Toast";
import toastStyles from "../../components/Toast/styles";
import { useTranslation } from "react-i18next";
import FilterDrawer from "./FilterDrawer";

const getClientList = async (
  page: number,
  search?: string,
  sortBy?: keyof IClient,
  sortOrder?: "asc" | "desc" | string,
  filters?: {
    name: string; phone: string; email: string; companyTaxId: string;address: string, postalCode:string, status: string
  }
): Promise<Error | IClientsRes> => {
  let url = `api/v1/clients?pageIndex=${page}&pageSize=10&sortBy=${sortBy ?? "createdAt"
    }&sortOrder=${sortOrder ?? "desc"}`;

  if (search) {
    url = `${url}&search=${search}`;
  }

  if (filters?.name) {
    url = `${url}&name=${filters.name}`;
  }

  if (filters?.phone) {
    url = `${url}&phone=${filters.phone}`;
  }

  if (filters?.email) {
    url = `${url}&email=${filters.email}`;
  }

  if (filters?.companyTaxId) {
    url = `${url}&companyTaxId=${filters.companyTaxId}`;
  }
    if (filters?.address) {
    url = `${url}&address=${filters.address}`;
  }
    if (filters?.postalCode) {
    url = `${url}&postalCode=${filters.postalCode}`;
  }
  if (filters?.status) {
    url = `${url}&status=${filters.status}`;
  }

  try {
    const resultData: AxiosResponse<IClientsRes> = await getApi(
      `${getAPIUrl()}/${url}`
    );
    return resultData.data;
  } catch (error) {
    return error as Error;
  }
};

const Clients = () => {
  const [page, setPage] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>("");
  const [fetchAgain, setFetchAgain] = useState(false);
  const [errorToast, setErrorToast] = useState<boolean>(false);
  const [toastErrorMsg, setErrorToastMsg] = useState<string>("");
  const [successToast, setSuccessToast] = useState<boolean>(false);
  const [toastSuccessMsg, setSuccessToastMsg] = useState<string>("");
  const [rowId, setRowId] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({ name: "", phone: "", email: "", companyTaxId: "", address:"",postalCode:"", status: "" });

  const handleClose = () => {
    setRowId("");
    setAnchorEl(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setRowId(id);
    setAnchorEl(event.currentTarget);
  };
  const navigate = useNavigate();

  const { t } = useTranslation();

  const handleEditAction = (id: string) => {
    navigate(`/edit-client/${id}`);
  };

  // Toggle Drawer
  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const handleDeleteAction = async (id: string) => {
    try {
      const res = await deleteApiWithAuth(
        `${getAPIUrl()}/api/v1/clients/${id}`
      );
      setSuccessToastMsg(res.data.message);
      setSuccessToast(true);
      setRowId("");
      setAnchorEl(null);
      setFetchAgain(!fetchAgain);
    } catch (error) {
      if (error instanceof Error) {
        const errorMsg = getApiErrorMessage(error);
        setErrorToastMsg(errorMsg);
        setErrorToast(true);
      }
    }
  };

  const columns: IColumn<IClient>[] = [
    { dataKey: "name", title: "Name", sortable: true },
    { dataKey: "phone", title: "Phone", sortable: true },
    { dataKey: "email", title: "Email", sortable: true },
    { dataKey: "companyTaxId", title: "Company Tax Id", sortable: true },
    { dataKey: "address", title: "Address", sortable: true },
    { dataKey: "postalCode", title: "Postal Code", sortable: true },

    {
      dataKey: "status",
      title: "Status",
      sortable: true,

      render: ({ row }: { row: IClient }) => {
        const status = UserStatuses.find(
          (statusObj) => statusObj.name === row.status
        );
        if (status) {
          return (
            <Typography
              component="span"
              sx={{
                color: status.color,
                fontWeight: 500,
                lineHeight: "1.125rem",
                fontSize: "0.75rem",
              }}
            >
              {status.name}
            </Typography>
          );
        } else {
          return null;
        }
      },
    },

    {
      dataKey: "id",
      title: "Actions",
      sortable: false,
      render: ({ row }: { row: IClient }): ReactElement => {
        const actionItems: MenuItems[] = [
          {
            item: (
              <Grid sx={styles.actionItemGrid}>
                <img alt="edit-icon" src={EditIcon} style={styles.actionIcon} />
                <Typography sx={styles.actionText("#1B1C1B")}>
                  {t("editData")}
                </Typography>
              </Grid>
            ),
            handleClick: () => handleEditAction(row.id),
          },
          {
            item: (
              <Grid sx={styles.actionItemGrid}>
                <img
                  alt="terminate-icon"
                  src={TerminateIcon}
                  style={styles.actionIcon}
                />
                <Typography sx={styles.actionText("#EF4444")}>
                  {t("terminate")}
                </Typography>
              </Grid>
            ),
            handleClick: () => handleDeleteAction(row.id),
          },
        ];
        return (
          <Box key={row.id}>
            <IconButton key={row.id} onClick={(e) => handleClick(e, row.id)}>
              {<img alt="more" src={MoreIcon} style={styles.actionIcon} />}
            </IconButton>
            {rowId === row.id && (
              <SimpleMenu
                id={row.id}
                anchorEl={anchorEl}
                handleCloseMenu={handleClose}
                menuItems={actionItems}
              />
            )}
          </Box>
        );
      },
    },
  ];

  const handleSearchChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setSearchValue(val);
    setPage(1);
    setFetchAgain(!fetchAgain);
  };

  const handleClearSearch = async () => {
    setSearchValue("");
    setPage(1);
    setFetchAgain(!fetchAgain);
  };



  const handleAdd = async () => {
    navigate("/add-client");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header
        name={t("clients")}
        setErrorToast={setErrorToast}
        setSuccessToast={setSuccessToast}
        setErrorToastMsg={setErrorToastMsg}
        setSuccessToastMsg={setSuccessToastMsg}
      />
      <Box sx={styles.tableContainer}>
        <TableHeader
          headerTitle={t("client")}
          headerDesc={t("client")}
          handleClearSearch={handleClearSearch}
          handleSearchChange={handleSearchChange}
          buttonName={t("addClient")}
          searchValue={searchValue}
          handleAdd={handleAdd}
          disableAdd={false}
          onFilterClick={toggleDrawer(true)}
        />
        <FilterDrawer
          drawerOpen={drawerOpen}
          toggleDrawer={toggleDrawer}
          handleApplyFilters={(filters) => {
            setFilters(filters);
            setPage(1);
            setFetchAgain(!fetchAgain);
          }}
        />


        <CustomTable
          page={page}
          setPage={setPage}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          fetchAgain={fetchAgain}
          setErrorToast={setErrorToast}
          setErrorToastMsg={setErrorToastMsg}
          columns={columns}
          rowKey="id"
          initialSortField={"createdAt"}
          fetchTableData={(page, search, sortField, sortDirection) =>
            getClientList(page, search, sortField, sortDirection, filters)
          }
        />
      </Box>
      <Toast
        id="get-clients-error"
        open={errorToast}
        message={toastErrorMsg}
        severity="error"
        onClose={() => setErrorToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        alertStyles={toastStyles.toastText}
      />
      <Toast
        id="clients-success"
        open={successToast}
        message={toastSuccessMsg}
        severity="success"
        onClose={() => setSuccessToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        alertStyles={toastStyles.toastText}
      />
    </Box>
  );
};

export default Clients;
